import type { SalesHierarchyNode } from "../types"

export interface TopDownPreviewUpdate {
  data_point_id: string
  node_id: string
  period: string
  cases_sold: number
  volume_dollars: string | number
  source_kind: string
  manual_note: string
}

/** Parse revenue typed in the top-down field (allows $, commas, spaces). */
export function parse_top_down_target_input(raw: string): number | null {
  const normalized = raw
    .trim()
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .replace(/\s+/g, "")
  if (!normalized) {
    return null
  }
  const n = Number(normalized)
  if (!Number.isFinite(n) || n <= 0) {
    return null
  }
  return n
}

function parse_volume(volume_dollars: string | number): number {
  if (typeof volume_dollars === "number") {
    return volume_dollars
  }
  const n = parseFloat(volume_dollars)
  return Number.isFinite(n) ? n : 0
}

/** Apply server top-down preview rows onto leaf sales_data (matches DB data_point id). */
export function mergeTopDownPreviewIntoHierarchy(
  nodes: SalesHierarchyNode[],
  updates: TopDownPreviewUpdate[]
): SalesHierarchyNode[] {
  const by_data_point_id = new Map(updates.map((u) => [u.data_point_id, u]))

  function walk(node: SalesHierarchyNode): SalesHierarchyNode {
    const children = node.children?.map((child: any) => walk(child as SalesHierarchyNode))
    const sales_data = (node.sales_data || []).map((dp: any) => {
      const uid = dp.id as string | undefined
      if (!uid || !by_data_point_id.has(uid)) {
        return dp
      }
      const u = by_data_point_id.get(uid)!
      const vol = parse_volume(u.volume_dollars)
      const before_metrics = {
        store_count: Number(dp.metrics?.store_count ?? 0),
        volume_dollars: Number(dp.metrics?.volume_dollars ?? 0),
        cases_sold: Number(dp.metrics?.cases_sold ?? 0),
        case_cost: Number(dp.metrics?.case_cost ?? 0),
        ...(dp.metrics?.units_sold !== undefined && dp.metrics?.units_sold !== null
          ? { units_sold: dp.metrics.units_sold }
          : {}),
      }
      const after_metrics = {
        ...before_metrics,
        cases_sold: u.cases_sold,
        volume_dollars: vol,
      }
      const vol_var = vol - before_metrics.volume_dollars
      const vol_pct =
        before_metrics.volume_dollars !== 0
          ? (vol_var / before_metrics.volume_dollars) * 100
          : 0
      const cases_var = u.cases_sold - before_metrics.cases_sold
      const cases_pct =
        before_metrics.cases_sold !== 0
          ? (cases_var / before_metrics.cases_sold) * 100
          : 0
      return {
        ...dp,
        metrics: after_metrics,
        forecast_metrics: before_metrics,
        actual_metrics: after_metrics,
        variance: {
          store_count_variance: 0,
          volume_dollars_variance: vol_var,
          cases_sold_variance: cases_var,
          store_count_variance_pct: 0,
          volume_dollars_variance_pct: vol_pct,
          cases_sold_variance_pct: cases_pct,
        },
        compare_axis: "dollars" as const,
        source_kind: u.source_kind,
        manual_note: u.manual_note,
        is_forecast: u.source_kind !== "actual",
        is_actual: u.source_kind === "actual",
      }
    })
    return {
      ...node,
      sales_data,
      children,
    } as SalesHierarchyNode
  }

  return nodes.map((n) => walk(n))
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("parse_top_down_target_input", () => {
    it("accepts commas and dollar sign", () => {
      expect(parse_top_down_target_input("24,000,000")).toBe(24_000_000)
      expect(parse_top_down_target_input("$ 24,000,000 ")).toBe(24_000_000)
    })
    it("rejects empty and non-positive", () => {
      expect(parse_top_down_target_input("")).toBeNull()
      expect(parse_top_down_target_input("0")).toBeNull()
      expect(parse_top_down_target_input("abc")).toBeNull()
    })
  })

  describe("mergeTopDownPreviewIntoHierarchy", () => {
    it("updates a leaf row by data_point id", () => {
      const nodes: SalesHierarchyNode[] = [
        {
          id: "node-1",
          name: "Leaf",
          level: "sku" as any,
          sales_data: [
            {
              period: { period_key: "2026-03", year: 2026, month: 3 },
              metrics: { store_count: 1, cases_sold: 10, volume_dollars: 100, case_cost: 10 },
              source_kind: "forecast",
              id: "dp-1",
            } as any,
          ],
        },
      ]
      const next = mergeTopDownPreviewIntoHierarchy(nodes, [
        {
          data_point_id: "dp-1",
          node_id: "node-1",
          period: "2026-03",
          cases_sold: 20,
          volume_dollars: "200",
          source_kind: "top_down_allocated",
          manual_note: "Allocated",
        },
      ])
      expect(next[0].sales_data[0].metrics.volume_dollars).toBe(200)
      expect(next[0].sales_data[0].metrics.cases_sold).toBe(20)
      expect(next[0].sales_data[0].source_kind).toBe("top_down_allocated")
      expect(next[0].sales_data[0].forecast_metrics?.volume_dollars).toBe(100)
      expect(next[0].sales_data[0].actual_metrics?.volume_dollars).toBe(200)
      expect(next[0].sales_data[0].compare_axis).toBe("dollars")
    })
  })
}
